import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
  type User as FirebaseUser,
} from 'firebase/auth'
import { doc, getDoc } from 'firebase/firestore'
import { auth, db } from '@/firebase'
import type { User } from '@/types'

export const useAuthStore = defineStore('auth', () => {
  const firebaseUser = ref<FirebaseUser | null>(null)
  const loading = ref(true)
  const isAdmin = ref(false)
  // Profil stocké dans Firestore (users/{uid}). C'est LA source de vérité pour
  // le nom affiché et la photo : c'est ce que l'admin modifie. On le lit à la
  // connexion pour que les changements côté admin soient bien pris en compte.
  const profile = ref<{ displayName: string | null; photoURL: string | null } | null>(null)
  // Compteur qui force le recalcul de `user` après une modif de profil.
  // (Firebase met à jour l'objet "en place" ; Vue ne le voit pas tout seul.)
  const profileNonce = ref(0)

  const user = computed<User | null>(() => {
    // Lecture du nonce → recalcul à chaque modif de profil.
    void profileNonce.value
    if (!firebaseUser.value) return null
    return {
      uid: firebaseUser.value.uid,
      email: firebaseUser.value.email,
      // Priorité au profil Firestore (modifiable par l'admin), puis repli sur
      // le profil d'authentification.
      displayName: profile.value?.displayName ?? firebaseUser.value.displayName,
      photoURL: profile.value?.photoURL ?? firebaseUser.value.photoURL,
    }
  })

  const isAuthenticated = computed(() => !!firebaseUser.value)

  /**
   * Charge le profil Firestore users/{uid} : rôle admin + nom affiché + photo.
   * Un seul accès à la base pour tout récupérer.
   */
  async function loadUserProfile(uid: string) {
    try {
      const snap = await getDoc(doc(db, 'users', uid))
      if (snap.exists()) {
        const data = snap.data()
        isAdmin.value = data?.isAdmin === true
        profile.value = {
          displayName: (data?.displayName as string) ?? null,
          photoURL: (data?.photoURL as string) ?? null,
        }
      } else {
        isAdmin.value = false
        profile.value = null
      }
    } catch {
      isAdmin.value = false
      profile.value = null
    }
    profileNonce.value++
  }

  function init() {
    return new Promise<void>((resolve) => {
      onAuthStateChanged(auth, async (fbUser) => {
        firebaseUser.value = fbUser
        if (fbUser) {
          await loadUserProfile(fbUser.uid)
        } else {
          isAdmin.value = false
          profile.value = null
        }
        loading.value = false
        resolve()
      })
    })
  }

  async function login(email: string, password: string) {
    const credential = await signInWithEmailAndPassword(auth, email, password)
    firebaseUser.value = credential.user
    await loadUserProfile(credential.user.uid)
  }

  async function logout() {
    await signOut(auth)
    firebaseUser.value = null
    isAdmin.value = false
    profile.value = null
  }

  /** Met à jour le nom affiché et/ou la photo de profil de l'utilisateur connecté. */
  async function updateUserProfile(data: { displayName?: string; photoURL?: string }) {
    if (!auth.currentUser) throw new Error('Aucun utilisateur connecté')
    await updateProfile(auth.currentUser, data)
    // On met aussi à jour la copie locale du profil Firestore pour que l'UI
    // reflète le changement immédiatement (le champ Firestore lui-même est
    // enregistré par l'appelant, cf. ProfilePage.persistToFirestore).
    if (profile.value) {
      if (data.displayName !== undefined) profile.value.displayName = data.displayName
      if (data.photoURL !== undefined) profile.value.photoURL = data.photoURL
    } else {
      profile.value = {
        displayName: data.displayName ?? null,
        photoURL: data.photoURL ?? null,
      }
    }
    // On force Vue à relire le profil (sinon l'UI ne se met pas à jour).
    profileNonce.value++
  }

  /**
   * Change le mot de passe. Firebase exige une connexion récente : on
   * ré-authentifie d'abord avec le mot de passe actuel, puis on enregistre le
   * nouveau. Cela évite l'erreur "requires-recent-login".
   */
  async function changePassword(currentPassword: string, newPassword: string) {
    if (!auth.currentUser?.email) throw new Error('Aucun utilisateur connecté')
    const credential = EmailAuthProvider.credential(auth.currentUser.email, currentPassword)
    await reauthenticateWithCredential(auth.currentUser, credential)
    await updatePassword(auth.currentUser, newPassword)
  }

  return {
    user, firebaseUser, loading, isAuthenticated, isAdmin,
    init, login, logout, updateUserProfile, changePassword,
  }
})
